import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    UserCircleIcon,
    ChatBubbleLeftRightIcon,
    ClipboardDocumentCheckIcon,
    LifebuoyIcon,
    CalendarDaysIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';

const REVIEW_TYPES = [
    { value: 'hours', label: 'Hours review' },
    { value: 'evidence', label: 'Evidence review' },
    { value: 'task', label: 'Task review' },
    { value: 'assessment', label: 'Project / assessment review' },
    { value: 'project', label: 'Project review' },
    { value: 'other', label: 'Other' }
];

const HELP_CATEGORIES = [
    { value: 'business_interaction', label: 'Business interaction help' },
    { value: 'technical', label: 'Technical help' },
    { value: 'mentor_support', label: 'Mentor support' },
    { value: 'uncomfortable_interaction', label: 'Uncomfortable interaction' },
    { value: 'other', label: 'Other' }
];

export default function MyMentor() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [reviewType, setReviewType] = useState('hours');
    const [reviewMessage, setReviewMessage] = useState('');
    const [sendingReview, setSendingReview] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [helpForm, setHelpForm] = useState({
        category: 'mentor_support',
        subject: '',
        description: ''
    });
    const [sendingHelp, setSendingHelp] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/student/mentor');
            setData(res.data.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to load mentor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const requestReview = async (e) => {
        e.preventDefault();
        try {
            setSendingReview(true);
            await axios.post('/student/mentor/request-review', {
                request_type: reviewType,
                message: reviewMessage
            });
            toast.success('Review request sent to your mentor');
            setReviewMessage('');
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not send review request');
        } finally {
            setSendingReview(false);
        }
    };

    const submitHelp = async (e) => {
        e.preventDefault();
        try {
            setSendingHelp(true);
            await axios.post('/student/support', helpForm);
            toast.success('Support request submitted');
            setHelpForm({ category: 'mentor_support', subject: '', description: '' });
            setShowHelp(false);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not submit support request');
        } finally {
            setSendingHelp(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-48" />
                <div className="skeleton h-40" />
                <span className="sr-only">Loading your mentor…</span>
            </div>
        );
    }

    if (!data?.mentor) {
        return (
            <div className="w-full max-w-3xl space-y-6">
                <PageHeader title="Mentor" description="Your assigned mentor and review requests." />
                <EmptyState
                    icon={UserCircleIcon}
                    title={data?.emptyState?.title || 'Mentor assignment pending'}
                    description={data?.emptyState?.message || 'You will see your assigned mentor here once matching is complete.'}
                    action={
                        <div className="flex justify-center gap-3">
                            <Link to="/help" className="btn-secondary">Help & Support</Link>
                            <Link to="/ai-coach" className="btn-primary">Talk to AI Coach</Link>
                        </div>
                    }
                />
            </div>
        );
    }

    const { mentor, feedback, recommendations, sessions, upcomingAction, reviewRequests } = data;

    return (
        <div className="w-full space-y-6">
            <PageHeader title="Mentor" description={mentor.introduction} />

            <section className="card flex gap-4 items-start animate-fade-up">
                <UserCircleIcon className="h-14 w-14 text-brand-700 shrink-0" />
                <div>
                    <p className="text-xs uppercase tracking-wide text-ink-500">{mentor.role}</p>
                    <h2 className="text-xl font-semibold text-ink-950">{mentor.name}</h2>
                    {mentor.email && (
                        <p className="text-sm text-ink-600 mt-1">{mentor.email}</p>
                    )}
                </div>
            </section>

            {upcomingAction && (
                <section className="card-muted flex gap-3">
                    <CalendarDaysIcon className="h-6 w-6 text-brand-700" />
                    <div>
                        <h3 className="font-medium text-ink-950">Upcoming mentor action</h3>
                        <p className="text-sm text-ink-700 mt-1">{upcomingAction.title}</p>
                        {upcomingAction.scheduled_at && (
                            <p className="text-xs text-ink-500 mt-1">
                                {new Date(upcomingAction.scheduled_at).toLocaleString()}
                            </p>
                        )}
                    </div>
                </section>
            )}

            <div className="grid md:grid-cols-2 gap-6">
                <section className="card space-y-3">
                    <h3 className="font-medium text-ink-950 flex items-center gap-2">
                        <ChatBubbleLeftRightIcon className="h-5 w-5" />
                        Feedback for you
                    </h3>
                    {feedback?.length ? (
                        <ul className="space-y-3">
                            {feedback.map((f) => (
                                <li key={f.id} className="text-sm text-ink-700 border-b border-gray-100 pb-2">
                                    <p>{f.content}</p>
                                    <p className="text-xs text-ink-400 mt-1">
                                        {f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-ink-500">
                            No shared feedback yet. Private mentor notes stay private — only notes your mentor marks for you appear here.
                        </p>
                    )}
                    {recommendations?.length > 0 && (
                        <div className="pt-2 space-y-2">
                            <p className="text-xs font-medium uppercase text-ink-500">Recommendations</p>
                            {recommendations.map((r) => (
                                <div key={r.id} className="text-sm">
                                    <p className="font-medium text-ink-800">{r.title}</p>
                                    <p className="text-ink-600">{r.content}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <section className="card space-y-3">
                    <h3 className="font-medium text-ink-950">Meeting & action history</h3>
                    {sessions?.length ? (
                        <ul className="space-y-2">
                            {sessions.map((s) => (
                                <li key={s.id} className="text-sm border-b border-gray-100 pb-2">
                                    <p className="capitalize font-medium text-ink-800">{s.title}</p>
                                    <p className="text-xs text-ink-500">
                                        {s.status}
                                        {s.scheduled_at
                                            ? ` · ${new Date(s.scheduled_at).toLocaleString()}`
                                            : ''}
                                    </p>
                                    {s.agenda && <p className="text-ink-600 mt-1">{s.agenda}</p>}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-ink-500">No sessions logged yet.</p>
                    )}
                </section>
            </div>

            <section className="card space-y-4">
                <h3 className="font-medium text-ink-950 flex items-center gap-2">
                    <ClipboardDocumentCheckIcon className="h-5 w-5" />
                    Request Review
                </h3>
                <form onSubmit={requestReview} className="space-y-3">
                    <select
                        className="input-field"
                        value={reviewType}
                        onChange={(e) => setReviewType(e.target.value)}
                    >
                        {REVIEW_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                    <textarea
                        className="input-field min-h-[80px]"
                        placeholder="What should your mentor review? (no confidential client data)"
                        value={reviewMessage}
                        onChange={(e) => setReviewMessage(e.target.value)}
                    />
                    <button type="submit" className="btn-primary" disabled={sendingReview}>
                        {sendingReview ? 'Sending…' : 'Request Review'}
                    </button>
                </form>
                {reviewRequests?.length > 0 && (
                    <div className="text-xs text-ink-500 space-y-1">
                        <p className="font-medium text-ink-600">Recent requests</p>
                        {reviewRequests.slice(0, 5).map((r) => (
                            <p key={r.id}>
                                {r.request_type} · {r.status}
                            </p>
                        ))}
                    </div>
                )}
            </section>

            <section className="card space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="font-medium text-ink-950 flex items-center gap-2">
                        <LifebuoyIcon className="h-5 w-5" />
                        Need Help
                    </h3>
                    <button
                        type="button"
                        className="btn-secondary text-sm"
                        onClick={() => setShowHelp((v) => !v)}
                    >
                        {showHelp ? 'Close' : 'Open form'}
                    </button>
                </div>
                {showHelp && (
                    <form onSubmit={submitHelp} className="space-y-3">
                        <select
                            className="input-field"
                            value={helpForm.category}
                            onChange={(e) =>
                                setHelpForm({ ...helpForm, category: e.target.value })
                            }
                        >
                            {HELP_CATEGORIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                        <input
                            className="input-field"
                            placeholder="Subject"
                            value={helpForm.subject}
                            onChange={(e) =>
                                setHelpForm({ ...helpForm, subject: e.target.value })
                            }
                            required
                        />
                        <textarea
                            className="input-field min-h-[100px]"
                            placeholder="Describe what you need. Do not include passwords, customer data, or confidential records."
                            value={helpForm.description}
                            onChange={(e) =>
                                setHelpForm({ ...helpForm, description: e.target.value })
                            }
                            required
                        />
                        <button type="submit" className="btn-primary" disabled={sendingHelp}>
                            {sendingHelp ? 'Submitting…' : 'Submit support request'}
                        </button>
                    </form>
                )}
                <p className="text-xs text-ink-500">
                    For full FAQs and escalation guidance, visit{' '}
                    <Link to="/help" className="text-brand-700 underline">
                        Help & Support
                    </Link>
                    .
                </p>
            </section>
        </div>
    );
}
